import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { usePlan, formatPrice } from "@/lib/plan";

function LegalShell({ title, children, seo }) {
  return (
    <div className="min-h-screen flex flex-col bg-paper">
      {seo}
      <header className="border-b-2 border-ink bg-white">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2 font-cabinet font-bold"><ArrowLeft className="w-5 h-5" /> Fluent<span className="text-coral">Loop</span></Link>
        </div>
      </header>
      <main className="flex-1 max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-cabinet font-extrabold text-4xl mb-2">{title}</h1>
        <p className="text-sm text-gray-400 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
        <div className="prose prose-sm max-w-none space-y-4 text-gray-700 [&_h2]:font-cabinet [&_h2]:font-bold [&_h2]:text-xl [&_h2]:text-ink [&_h2]:mt-8">
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy"
      seo={<Seo title="Privacy Policy · FluentLoop" description="How FluentLoop handles your data. We never store your photos or voice — GDPR-first by design." />}>
      <h2>1. Our privacy promise</h2>
      <p>FluentLoop is built for EU users and follows GDPR by design. <strong>We never persist your raw photos or voice recordings.</strong> When you capture an image or record audio, it is processed in memory only and deleted immediately after the language engine extracts the derived result (a word, a score, or a transcript fragment).</p>
      <h2>2. What we store</h2>
      <p>Depending on which features you use, we store:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Account and profile information (email, name, sign-in method, subscription status).</li>
        <li>Your language-learning preferences and language profiles (languages, level, script, interface language).</li>
        <li>Vocabulary you save and its review (spaced-repetition) history.</li>
        <li>Learning progress: practice results, activity days, scores and XP.</li>
        <li>Personal learning signals used to plan your practice (your learning "Brain" and daily curriculum).</li>
        <li>Voice Journal entries: the text of your transcripts and the corrected versions, never the audio itself.</li>
        <li>Fluent Friend conversations (text).</li>
        <li>Lens results: text extracted from your images and the analysis, if you save a scan. Never the image itself.</li>
        <li>Stories you create, and assignment progress if you join a class.</li>
        <li>Consent records, payment records, and technical usage metadata (which AI feature was used, when, and whether it succeeded) for billing, limits and service reliability.</li>
      </ul>
      <p>No raw photos or voice recordings are ever written to disk or database.</p>
      <h2>3. Consent</h2>
      <p>We ask for explicit consent before accessing your camera or microphone, and log each grant with a policy version and timestamp.</p>
      <h2>4. Your rights</h2>
      <p>You can export all of your data or permanently delete your account at any time from Settings. Deletion is immediate and irreversible. We may keep anonymous usage statistics that can no longer be linked to you; Stripe keeps its own payment records as required by law.</p>
      <h2>5. Third parties</h2>
      <p>We use an AI language model provider to process language tasks and Stripe to process payments. We do not sell your data.</p>
      <h2>6. Contact</h2>
      <p>Questions? Reach us at privacy@getfluentloop.com.</p>
    </LegalShell>
  );
}

export function Terms() {
  const plan = usePlan();
  return (
    <LegalShell title="Terms of Service"
      seo={<Seo title="Terms of Service · FluentLoop" description="The terms governing your use of FluentLoop, the AI language-learning ecosystem." />}>
      <h2>1. Acceptance</h2>
      <p>By creating a FluentLoop account you agree to these Terms and our Privacy Policy.</p>
      <h2>2. The service</h2>
      <p>FluentLoop provides AI-powered language-learning tools. LanguageLoop is free with a daily cap; all other features and uncapped usage require an active {plan?.name || "GetFluentLoop Pro"} subscription{plan ? ` (${formatPrice(plan)}/month)` : ""}.</p>
      <h2>3. Subscriptions</h2>
      <p>{plan?.name || "GetFluentLoop Pro"} is a recurring monthly subscription{plan ? ` at ${formatPrice(plan)} per month` : ""}, billed via Stripe. The displayed subscription price includes applicable taxes where required. Any applicable tax component is calculated based on your location and shown at checkout. Your subscription renews automatically each month until you cancel. You may cancel at any time; cancellation takes effect at the end of the current paid period, and you keep Pro access until then. If a renewal payment fails, Pro access continues for up to 3 days while payment is retried.</p>
      <h2>4. Acceptable use</h2>
      <p>Don't misuse the service, attempt to bypass usage limits, or upload unlawful content.</p>
      <h2>5. AI-generated content</h2>
      <p>Language output is generated by AI and may occasionally be imperfect. Use your judgement.</p>
      <h2>6. Termination</h2>
      <p>You may delete your account at any time. We may suspend accounts that violate these Terms.</p>
      <h2>7. Contact</h2>
      <p>Questions? Reach us at legal@getfluentloop.com.</p>
    </LegalShell>
  );
}
